if (!customElements.get("m-recently-viewed")) {
  class MRecentlyViewed extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
      };

      this.initialized = false;
      this.products = 0;
      this.slider = null;
      this.swiper = null;

      this.handleScreenChange = this.initByScreenSize.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.domNodes = this.getDomNodes(this.selectors, this);

      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = parseInt(this.dataset.productsPerRow, 10) || 4;
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.productsToShow = parseInt(this.dataset.productsToShow, 10) || 8;

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener("matchMobile", this.handleScreenChange);
      document.removeEventListener("unmatchMobile", this.handleScreenChange);

      if (this.swiper && typeof this.swiper.destroy === "function") {
        this.swiper.destroy(false, true);
      }

      this.slider = null;
      this.swiper = null;
      this.initialized = false;
    }

    getDomNodes(selectors, scope) {
      if (typeof queryDomNodes === "function") {
        return queryDomNodes(selectors, scope);
      }

      return Object.keys(selectors).reduce((nodes, key) => {
        nodes[key] = scope.querySelector(selectors[key]);
        return nodes;
      }, {});
    }

    init() {
      const queryString = this.getQueryString();

      if (!queryString) {
        this.classList.add("m:hidden");
        return;
      }

      fetch(this.dataset.url + queryString)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Recently viewed request failed: ${response.status}`);
          }

          return response.text();
        })
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;

          const recommendations = html.querySelector("m-recently-viewed");
          if (!recommendations || !recommendations.innerHTML.trim()) {
            this.classList.add("m:hidden");
            return;
          }

          this.products = recommendations.querySelectorAll(".m-product-card").length;

          if (this.products <= 0) {
            this.classList.add("m:hidden");
            return;
          }

          this.innerHTML = recommendations.innerHTML;
          this.classList.remove("m:hidden");

          this.initByScreenSize();
          this.bindScreenEvents();
          this.refreshProductActions();
        })
        .catch((error) => {
          console.error(error);
          this.classList.add("m:hidden");
        });
    }

    bindScreenEvents() {
      document.removeEventListener("matchMobile", this.handleScreenChange);
      document.removeEventListener("unmatchMobile", this.handleScreenChange);

      document.addEventListener("matchMobile", this.handleScreenChange);
      document.addEventListener("unmatchMobile", this.handleScreenChange);
    }

    refreshProductActions() {
      if (window.MinimogTheme) {
        window.MinimogTheme.CompareProduct?.setCompareButtonsState?.();
        window.MinimogTheme.Wishlist?.setWishlistButtonsState?.();
      }
    }

    initByScreenSize() {
      if (!this.enableSlider) return;

      const { slideContainer, slideControls } = this.getDomNodes(this.selectors, this);
      if (!slideContainer) return;

      const isMobile = !!(window.MinimogTheme && window.MinimogTheme.config && window.MinimogTheme.config.mqlMobile);

      if (isMobile && this.mobileDisableSlider) {
        if (slideControls) slideControls.classList.add("m:hidden");

        slideContainer.classList.remove("swiper-container");

        if (this.swiper && typeof this.swiper.destroy === "function") {
          this.swiper.destroy(false, true);
          this.swiper = null;
          this.slider = null;
        }

        return;
      }

      slideContainer.classList.add("swiper-container");

      if (this.products <= this.items) {
        if (slideControls) slideControls.classList.add("m:hidden");
      } else if (slideControls) {
        slideControls.classList.remove("m:hidden");
      }

      this.initSlider();
    }

    initSlider() {
      const { slideContainer } = this.getDomNodes(this.selectors, this);
      if (!slideContainer || !window.MinimogLibs || !window.MinimogLibs.Swiper) return;

      const wrapper = slideContainer.querySelector(".swiper-wrapper");
      if (!wrapper) return;

      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer ? controlsContainer.querySelector(".m-slider-controls__button-prev") : null;
      const nextButton = controlsContainer ? controlsContainer.querySelector(".m-slider-controls__button-next") : null;
      const slideItemsLength = wrapper.childElementCount;

      if (slideContainer.swiper) {
        slideContainer.swiper.update();

        this.swiper = slideContainer.swiper;
        this.slider = slideContainer.swiper;

        this.updateControlsVisibility(slideItemsLength, controlsContainer);
        return;
      }

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.products > this.items,
        pagination: this.showPagination
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: this.items >= 3 ? 3 : this.items,
          },
          1024: {
            slidesPerView: this.items >= 4 ? 4 : this.items,
          },
          1280: {
            slidesPerView: this.items,
          },
        },
        threshold: 2,
        on: {
          init: () => {
            window.setTimeout(() => {
              this.updateControlsOffset(controlsContainer, prevButton, nextButton);
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (!controlsContainer) return;

            const slidesPerView = breakpointParams.slidesPerView || this.items;

            if (slideItemsLength > slidesPerView) {
              controlsContainer.classList.remove("m:hidden");
              swiper.allowTouchMove = true;
            } else {
              controlsContainer.classList.add("m:hidden");
              swiper.allowTouchMove = false;
            }
          },
        },
      });

      this.swiper = slideContainer.swiper || this.slider;

      if (this.swiper && this.showNavigation) {
        if (prevButton) {
          prevButton.addEventListener("click", () => this.swiper.slidePrev());
        }

        if (nextButton) {
          nextButton.addEventListener("click", () => this.swiper.slideNext());
        }
      }
    }

    updateControlsOffset(controlsContainer, prevButton, nextButton) {
      if (!controlsContainer) return;

      const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
      if (!firstItem) return;

      const itemHeight = firstItem.clientHeight;

      if (itemHeight > 0) {
        controlsContainer.style.setProperty("--offset-top", `${parseInt(itemHeight, 10) / 2}px`);
      }

      if (prevButton) prevButton.classList.remove("m:hidden");
      if (nextButton) nextButton.classList.remove("m:hidden");
    }

    updateControlsVisibility(slideItemsLength, controlsContainer) {
      if (!controlsContainer) return;

      if (slideItemsLength <= this.items) {
        controlsContainer.classList.add("m:hidden");
      } else {
        controlsContainer.classList.remove("m:hidden");
      }
    }

    getQueryString() {
      const cookieName = "minimog-recently-viewed";
      let items = [];

      try {
        items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
      } catch (error) {
        items = [];
      }

      if (!Array.isArray(items) || !items.length) {
        return "";
      }

      const currentProductId = this.dataset.productId ? parseInt(this.dataset.productId, 10) : null;

      if (currentProductId && items.includes(currentProductId)) {
        items = items.filter((item) => item !== currentProductId);
      }

      return items
        .filter(Boolean)
        .slice(0, this.productsToShow)
        .map((item) => `id:${item}`)
        .join(" OR ");
    }
  }

  customElements.define("m-recently-viewed", MRecentlyViewed);
}